import { Box, Button, Card, CardContent, Stack, Typography, useTheme, Avatar } from "@mui/material";
import { useAuth } from "../../shared/auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { InternalLink } from "../../shared/components/InternalLink";

export default function Welcome() {
  const theme = useTheme();
  const { user, isAuthenticated, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If not authenticated after loading, redirect to login
    if (!loading && !isAuthenticated) {
      navigate("/login");
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading || !user) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", py: 4 }}>
      <Card
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.dark}15 100%)`,
          border: `2px solid ${theme.palette.success.main}`,
        }}
      >
        <CardContent>
          <Stack spacing={4} alignItems="center" sx={{ py: 4 }}>
            {/* Success Icon */}
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
              }}
            >
              <Avatar
                src={user.avatarUrl}
                alt={user.displayName || user.username}
                sx={{
                  width: 120,
                  height: 120,
                  border: `4px solid ${theme.palette.success.main}`,
                }}
              >
                {(user.displayName || user.username).charAt(0).toUpperCase()}
              </Avatar>
              <CheckCircleIcon
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  fontSize: 40,
                  color: theme.palette.success.main,
                  background: theme.palette.background.paper,
                  borderRadius: "50%",
                }}
              />
            </Box>

            {/* Welcome Message */}
            <Stack spacing={1} alignItems="center">
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.success.main,
                  textAlign: "center",
                }}
              >
                Welcome!
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  textAlign: "center",
                  color: theme.palette.text.primary,
                }}
              >
                {user.displayName || user.username}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: "center",
                }}
              >
                You're now authenticated and ready to play!
              </Typography>
            </Stack>

            {/* User Info */}
            <Card
              sx={{
                background: theme.palette.background.default,
                width: "100%",
                maxWidth: 400,
              }}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Username
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {user.username}
                    </Typography>
                  </Box>
                  {user.displayName && user.displayName !== user.username && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Display Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.displayName}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Discord ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                      {user.discordId}
                    </Typography>
                  </Box>
                  {user.role === "superuser" && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.warning.main }}
                      >
                        Role
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: theme.palette.warning.main }}
                      >
                        Superuser
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Stack spacing={2} sx={{ width: "100%", maxWidth: 400 }}>
              <Button
                variant="contained"
                size="large"
                component={InternalLink}
                to="/game"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  fontWeight: 600,
                  py: 1.5,
                }}
              >
                Browse Games
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={InternalLink}
                to="/match"
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.text.primary,
                  "&:hover": {
                    borderColor: theme.palette.primary.light,
                    background: "rgba(255, 63, 127, 0.1)",
                  },
                }}
              >
                View My Matches
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={() => logout()}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.error.main,
                  },
                }}
              >
                Log out
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
